import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// import libraries
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
//import classnames from 'classnames';
// import components
import { Button } from '../button/button';
import { ButtonVariant } from '../../core/enums';
import { GenericRelatedModal } from '../generic-related-modal/generic-related-modal';
import { DatePickerComponent } from '../../components/datepicker-component/datepicker-component';
import { attachDocsActions } from '@codametrix/ui-common';
import { CMXIcon, Conditional } from '@codametrix/ui-components';
import { DataGrid, LoadingSlugs } from '@codametrix/ui-components';
import { CheckboxMenu } from '../../components/checkbox-menu/checkbox-menu';
import { showFeedbackError } from '../../actions/ui';
// import styles
import './attach-docs-modal.scss';
/**
 * Definitions for columns in the mergeable cases grid.
 * @constant
 * @type {Array<Object>}
 */ const docsColsDefs = [
    {
        key: 'dateOfService',
        displayName: `Date`,
        isSortable: false
    },
    {
        key: 'documentType',
        displayName: `Type`,
        isSortable: false
    },
    {
        key: 'specialty',
        displayName: `Specialty`,
        isSortable: false
    },
    {
        key: 'providerNumber',
        displayName: `Provider`,
        isSortable: false
    }
];
/**
 * AttachDocsModal functional component.
 * @component
 * @returns {React.ReactNode} - The rendered component.
 */ export const AttachDocsModal = ()=>{
    const dispatch = useDispatch();
    const cancelAttachRef = useRef(null);
    const { isOpen, currentCaseRun, loadingNotes, activeNotes, documentsAvailable, specialtiesDictionary, noteTypesDictionary, loadingDocuments } = useSelector((state)=>{
        return state.attachDocs;
    });
    const [providersFilter, setProvidersFilter] = useState([]);
    const [specialtiesFilter, setSpecialtiesFilter] = useState([]);
    const [documentsTypeFilter, setDocumentTypeFilter] = useState([]);
    const [startDateFilter, setStartDateFilter] = useState(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
    const [endDateFilter, setEndDateFilter] = useState(new Date());
    const [idDocSelected, setIdDocSelected] = useState(0);
    /**
   * Close the merge cases modal.
   * @function
   */ const handleClose = ()=>{
        dispatch(attachDocsActions.hideAttachDocsModal());
    };
    /**
   * Handle selection of a doc
   * @function
   * @param {any} data - The data associated with the selected row.
   * @param {any} ev - The event object.
   */ const handleDocSelection = (data)=>{
        dispatch(attachDocsActions.downloadDocs(data));
        setIdDocSelected(data.id);
    };
    /**
   * Handle date clear
   */ const handleDateClear = ()=>{
        setStartDateFilter(null);
        setEndDateFilter(null);
    };
    /**
   * Fetch docs for the current MRN
   * @function
   */ useEffect(()=>{
        if (isOpen) {
            try {
                dispatch(attachDocsActions.fetchAvailableDocs(currentCaseRun));
            } catch (error) {
                dispatch(showFeedbackError({
                    message: 'There were issues getting documents.',
                    dismissable: true,
                    className: 'feedback-error'
                }));
            }
            try {
                dispatch(attachDocsActions.fetchSpecialtiesDictionary(currentCaseRun));
            } catch (error) {
                dispatch(showFeedbackError({
                    message: 'There were issues getting specialty dictionary.',
                    dismissable: true,
                    className: 'feedback-error'
                }));
            }
            try {
                dispatch(attachDocsActions.fetchNoteTypeDictionary(currentCaseRun));
            } catch (error) {
                dispatch(showFeedbackError({
                    message: 'There were issues getting note type dictionary.',
                    dismissable: true,
                    className: 'feedback-error'
                }));
            }
        }
    // eslint-disable-next-line
    }, [
        isOpen
    ]);
    const filteredDocumentsAvailable = documentsAvailable.filter((docAvailable)=>{
        let providerFilter = true;
        let specialtyFilter = true;
        let documentTypeFilter = true;
        let dateFilter = true;
        const specialty = docAvailable.specialty?.toString() ?? 'NOT_FOUND';
        if (providersFilter.length > 0) {
            providerFilter = providersFilter.map((providerFilter)=>providerFilter.label).includes(docAvailable.providerNumber.toString());
        }
        if (specialtiesFilter.length > 0) {
            specialtyFilter = specialtiesFilter.map((specialtyFilter)=>specialtyFilter.label).includes(specialty);
        }
        if (documentsTypeFilter.length > 0) {
            documentTypeFilter = documentsTypeFilter.map((documentTypeFilter)=>documentTypeFilter.label).includes(docAvailable.documentType.toString());
        }
        if (docAvailable?.dateOfService) {
            const dateOfService = new Date(docAvailable.dateOfService);
            dateFilter = !!startDateFilter && !!endDateFilter ? dateOfService >= startDateFilter && dateOfService <= endDateFilter : true;
        }
        return providerFilter && specialtyFilter && documentTypeFilter && dateFilter;
    });
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx(GenericRelatedModal, {
            contentTestId: "attach-docs-modal",
            className: "attach-docs-modal",
            isOpen: isOpen,
            handleClose: handleClose,
            showCloseIcon: true,
            leastDestructiveRef: cancelAttachRef,
            includePatientEncounter: true,
            notes: activeNotes,
            loadingNotes: loadingNotes,
            modalHeader: /*#__PURE__*/ _jsx("p", {
                children: "Attach documentation to this case."
            }),
            modalContent: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("h3", {
                        className: "attach-docs-title",
                        children: [
                            "These are the documents we found for MRN",
                            ' ',
                            /*#__PURE__*/ _jsx("b", {
                                children: currentCaseRun?.patient_identifier?.number
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "attach-docs-filters",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "attach-docs-date-filter",
                                children: /*#__PURE__*/ _jsx(DatePickerComponent, {
                                    startDate: startDateFilter,
                                    endDate: endDateFilter,
                                    label: '',
                                    showRange: true,
                                    onSelection: (startDate, endDate)=>{
                                        if (startDate && endDate) {
                                            setStartDateFilter(startDate);
                                            setEndDateFilter(endDate);
                                        }
                                        if (!startDate && !endDate) {
                                            handleDateClear();
                                        }
                                    },
                                    selectsRange: true
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "divider"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "note-type-filter-sec",
                                children: /*#__PURE__*/ _jsx(CheckboxMenu, {
                                    options: noteTypesDictionary.map((noteType)=>({
                                            label: noteType['Note Description'],
                                            value: noteType['Note Description']
                                        })),
                                    label: "Document Type",
                                    onApplyFilters: (docs)=>setDocumentTypeFilter(docs),
                                    selectedOptions: documentsTypeFilter,
                                    withTypeAhead: true,
                                    menuIcon: /*#__PURE__*/ _jsx(CMXIcon, {
                                        svgTitleText: "Filter by document type",
                                        svgIconName: "BiomeReports",
                                        svgIconSize: "20"
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "divider"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "note-specialty-filter-sec",
                                children: /*#__PURE__*/ _jsx(CheckboxMenu, {
                                    options: specialtiesDictionary.map((specialtyDictionary)=>({
                                            label: specialtyDictionary['Note Specialty'],
                                            value: specialtyDictionary['Note Specialty']
                                        })),
                                    label: "Specialty",
                                    onApplyFilters: (specialties)=>setSpecialtiesFilter(specialties),
                                    selectedOptions: specialtiesFilter,
                                    withTypeAhead: true,
                                    menuIcon: /*#__PURE__*/ _jsx(CMXIcon, {
                                        svgTitleText: "Filter by document type",
                                        svgIconName: "BiomeBriefCase",
                                        svgIconSize: "20"
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "divider"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "note-provider-filter-sec",
                                children: /*#__PURE__*/ _jsx(CheckboxMenu, {
                                    options: documentsAvailable.map((docAvailable)=>({
                                            label: docAvailable?.providerNumber?.toString(),
                                            value: docAvailable?.providerNumber?.toString()
                                        })),
                                    label: "Provider",
                                    onApplyFilters: (providers)=>setProvidersFilter(providers),
                                    selectedOptions: providersFilter,
                                    withTypeAhead: true,
                                    menuIcon: /*#__PURE__*/ _jsx(CMXIcon, {
                                        svgTitleText: "Filter by document type",
                                        svgIconName: "BiomeUserPlus",
                                        svgIconSize: "20"
                                    })
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(DataGrid, {
                        idField: "id",
                        idSelected: idDocSelected,
                        onRowSelection: handleDocSelection,
                        items: filteredDocumentsAvailable,
                        columnDefs: docsColsDefs,
                        selectable: true,
                        theme: 'chakra'
                    }),
                    /*#__PURE__*/ _jsx(Conditional, {
                        every: [
                            documentsAvailable.length === 0,
                            !loadingDocuments
                        ],
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "note-unavaialable",
                            children: "There are no documents available to attach."
                        })
                    }),
                    /*#__PURE__*/ _jsx(Conditional, {
                        every: [
                            filteredDocumentsAvailable.length === 0,
                            documentsAvailable.length > 0,
                            !loadingDocuments
                        ],
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "note-unavaialable",
                            children: "There are no documents available to attach with the current filter criteria."
                        })
                    }),
                    /*#__PURE__*/ _jsx(Conditional, {
                        on: loadingDocuments,
                        children: /*#__PURE__*/ _jsx(LoadingSlugs, {
                            numberItems: 7
                        })
                    })
                ]
            }),
            modalFooter: /*#__PURE__*/ _jsxs("div", {
                className: "action-buttons",
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        ref: cancelAttachRef,
                        variant: ButtonVariant.SECONDARY,
                        dataTestId: "cancelBtn",
                        label: "Cancel",
                        onClick: handleClose
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        dataTestId: "mergeBtn",
                        className: "merge-btn",
                        label: "Attach",
                        onClick: ()=>{}
                    })
                ]
            })
        })
    });
};
